import { useState, useEffect } from 'react';
import _ from 'lodash';

import LabeledInput from '../LabeledInput';
import { GroupInputs } from '../GroupInputs';

import * as api from '../../api';
import * as logger from '../../logger';

import defaultStyles from '../../styles/formSetStyles.module.css';
import { useTranslation } from 'react-i18next';

interface Props {
  value: string;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
}

export const ExerciseRoutine: React.FC<Props> = ({
  value,
  updateFieldValue,
  saveFieldValue,
}) => {
  const exerciseRoutineInit: Partial<api.PatientSocialHistory.ExerciseRoutine> =
    {};
  const [exerciseRoutine, setExerciseRoutine] = useState(exerciseRoutineInit);
  const { t } = useTranslation();

  useEffect(() => {
    try {
      if (!value) return;
      const parsedValue: api.PatientSocialHistory.ExerciseRoutine =
        JSON.parse(value);
      setExerciseRoutine(parsedValue);
    } catch (e) {
      logger.error(e, 'Error parsing exercise usage value');
    }
  }, [value]);

  if (_.isEmpty(exerciseRoutine)) {
    setExerciseRoutine({ daysPerWeek: 0, durationMinutes: 0 });
  }

  const exerciseRoutineUpdate = (update: {
    [key in keyof api.PatientSocialHistory.ExerciseRoutine]?: number;
  }) => {
    return JSON.stringify({ ...exerciseRoutine, ...update });
  };

  const saveFormField = (value: string) => {
    updateFieldValue(value);
    saveFieldValue(value);
  };

  if (!value) {
    saveFormField(JSON.stringify({ daysPerWeek: 0, durationMinutes: 0 }));
  }

  return (
    <GroupInputs>
      <LabeledInput
        label={t('# Days of strenuous activity / week')}
        htmlFor="exercise-days"
      >
        <div className={defaultStyles.InputWrapper}>
          <input
            id="exercise-days"
            className={defaultStyles.Input}
            onChange={(event) => {
              const newValue = +event.currentTarget.value;
              if (Number.isNaN(newValue) || newValue < 0 || newValue > 7) {
                return;
              }
              saveFormField(exerciseRoutineUpdate({ daysPerWeek: newValue }));
            }}
            min="0"
            max="7"
            pattern="[0-9]*"
            step={1}
            type="number"
            value={exerciseRoutine.daysPerWeek}
          />
        </div>
      </LabeledInput>
      <LabeledInput
        label={t('Number of minutes of activity')}
        htmlFor="exercise-minutes"
      >
        <div className={defaultStyles.InputWrapper}>
          <input
            id="exercise-minutes"
            className={defaultStyles.Input}
            onChange={(event) => {
              const newValue = +event.currentTarget.value;
              if (Number.isNaN(newValue) || newValue < 0 || newValue > 1440) {
                return;
              }
              saveFormField(
                exerciseRoutineUpdate({ durationMinutes: newValue }),
              );
            }}
            min="0"
            max="1440"
            pattern="[0-9]*"
            step={1}
            type="number"
            value={exerciseRoutine.durationMinutes}
          />
        </div>
      </LabeledInput>
    </GroupInputs>
  );
};
